/**
 * Storage Configuration API Client
 * Handles R2/S3 storage configuration for tenants
 */

import apiClient from './client';

export interface StorageConfig {
  id: string;
  tenant_id: string;
  provider: 'r2' | 's3';
  bucket_name: string;
  endpoint_url?: string;
  region: string;
  public_url_prefix?: string;
  storage_used_bytes: number;
  file_count: number;
  max_file_size_mb: number;
  allowed_file_types: string;
  is_active: boolean;
  has_credentials: boolean;
  created_at: string;
  updated_at: string;
}

export interface StorageConfigInput {
  provider: 'r2' | 's3';
  bucket_name: string;
  access_key_id: string;
  secret_access_key: string;
  endpoint_url?: string;
  region?: string;
  public_url_prefix?: string;
  max_file_size_mb?: number;
  allowed_file_types?: string;
}

export interface TenantFile {
  id: string;
  file_key: string;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  url: string;
  created_at: string;
}

export interface UploadedFile {
  id: string;
  key: string;
  name: string;
  type: string;
  size: number;
  url: string;
}

export const storageApi = {
  /**
   * Get storage configuration for current tenant or specified tenant (super admin)
   */
  getConfig: async (tenantId?: string): Promise<StorageConfig | null> => {
    const params = tenantId ? { tenant_id: tenantId } : {};
    const response = await apiClient.get('/v1/storage/config', { params });
    return response.data.config;
  },

  /**
   * Save storage configuration for current tenant or specified tenant (super admin)
   */
  saveConfig: async (data: StorageConfigInput, tenantId?: string): Promise<void> => {
    const payload = tenantId ? { ...data, tenant_id: tenantId } : data;
    await apiClient.post('/v1/storage/config', payload);
  },

  /**
   * Test storage connection for current tenant or specified tenant (super admin)
   */
  testConnection: async (tenantId?: string): Promise<{ success: boolean; error?: string }> => {
    const payload = tenantId ? { tenant_id: tenantId } : {};
    const response = await apiClient.post('/v1/storage/test', payload);
    return response.data;
  },

  /**
   * Upload a file
   */
  upload: async (file: File): Promise<UploadedFile> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/v1/storage/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.file;
  },

  /**
   * List files
   */
  listFiles: async (params?: {
    prefix?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ files: TenantFile[]; total: number }> => {
    const response = await apiClient.get('/v1/storage/files', { params });
    return response.data;
  },

  /**
   * Delete a file
   */
  deleteFile: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/storage/files/${id}`);
  },

  /**
   * Get proper download URL for a file
   * Converts relative URLs to full API URLs
   */
  getFileUrl: (file: TenantFile): string => {
    // If URL is already absolute (starts with http), return as-is
    if (file.url.startsWith('http://') || file.url.startsWith('https://')) {
      return file.url;
    }

    // Get the base URL and remove /api suffix if present
    // Files are served from the worker root, not under /api
    const baseUrl = (apiClient.defaults.baseURL || '').replace(/\/api$/, '');

    // If URL starts with /, it's relative to the worker root
    if (file.url.startsWith('/')) {
      return `${baseUrl}${file.url}`;
    }

    // Otherwise prepend the worker URL
    return `${baseUrl}/${file.url}`;
  },
};

export default storageApi;
